// ============================================================================
// CLIENT DASHBOARD REPOSITORY
// ARQUIVO: src/services/ClientDashboardRepository.js
// RESPONSABILIDADE: Operações Firestore do ClientDashboard (portal do cliente).
// Criado: Fase 6.3 — mover Firestore direto de ClientDashboard.jsx para repositório.
// ============================================================================

import {
  addDoc, collection, doc, limit, onSnapshot, query, serverTimestamp, updateDoc, where
} from 'firebase/firestore';
import { db } from '../shared/api/firebase/config';
import { FIREBASE_CONFIG } from '../config/Config';

const APP_ID = FIREBASE_CONFIG?.projectId || 'portalrda-f312d';

/** Adiciona nota ao cliente. */
export async function addClientNote(clientId, noteData) {
  await addDoc(
    collection(db, 'artifacts', APP_ID, 'public', 'data', 'clients', clientId, 'notes'),
    {
      ...noteData,
      createdAt: serverTimestamp(),
    }
  );
}

/** Observa cases (workspaces) do cliente. */
export function watchClientCases(clientId, onData, onError) {
  const col = collection(db, 'artifacts', APP_ID, 'public', 'data', 'autos_workspaces');
  return onSnapshot(
    query(col, where('caseTeamIds', 'array-contains', clientId)),
    (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }))),
    onError
  );
}

/** Observa workspaces do cliente (direto + co-parte). */
export function watchClientWorkspaces(clientId, onDirect, onCoParty, onError) {
  const col = collection(db, 'artifacts', APP_ID, 'public', 'data', 'autos_workspaces');
  const unsub1 = onSnapshot(
    query(col, where('clientId', '==', clientId)),
    (snap) => onDirect(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }))),
    onError
  );
  const unsub2 = onSnapshot(
    query(col, where('partiesRefIds', 'array-contains', clientId)),
    (snap) => onCoParty(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }))),
    onError
  );
  return () => { unsub1(); unsub2(); };
}

/** Observa documentos pendentes de assinatura. */
export function watchPendingSignatures(clientId, onData, onError) {
  const sources = [];
  sources.push(onSnapshot(
    query(collection(db, 'documents'), where('clientId', '==', clientId), limit(200)),
    (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }))),
    onError
  ));
  sources.push(onSnapshot(
    query(collection(db, 'artifacts', APP_ID, 'public', 'data', 'documents'), where('clientId', '==', clientId), limit(200)),
    (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }))),
    onError
  ));
  return () => sources.forEach((u) => { try { u(); } catch {} });
}

/** Observa invoices pendentes. */
export function watchPendingInvoices(clientId, onData, onError) {
  const sources = [];
  sources.push(onSnapshot(
    query(collection(db, 'invoices'), where('clientId', '==', clientId), where('status', '==', 'PENDING'), limit(50)),
    (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }))),
    onError
  ));
  sources.push(onSnapshot(
    query(collection(db, 'artifacts', APP_ID, 'public', 'data', 'invoices'), where('clientId', '==', clientId), where('status', '==', 'PENDING'), limit(50)),
    (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }))),
    onError
  ));
  return () => sources.forEach((u) => { try { u(); } catch {} });
}

/** Observa document requests do cliente. */
export function watchDocumentRequests(clientId, onData, onError) {
  const qq = query(collection(db, 'document_requests'), where('clientId', '==', clientId), limit(50));
  return onSnapshot(qq, (snap) => {
    onData(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
  }, onError);
}

/** Adiciona documento à triagem. */
export async function addToTriage(triagePayload) {
  const payload = {
    ...triagePayload,
    createdAt: serverTimestamp(),
  };
  try {
    await addDoc(collection(db, 'rda_triage'), payload);
  } catch (err) {
    if (!APP_ID) throw err;
    await addDoc(collection(db, 'artifacts', APP_ID, 'public', 'data', 'rda_triage'), payload);
  }
}

/** Atualiza status de document request. */
export async function updateDocumentRequestStatus(itemRef, data) {
  await updateDoc(itemRef, {
    ...data,
    updatedAt: serverTimestamp(),
    lastUploadAt: serverTimestamp(),
  });
}
